/**
 * PortalContactPicker: choose the person a quote, proof or order is for.
 *
 * Offers, in this order:
 *   1. people already at the customer (the host page's own list and the
 *      lookup's, so the picker never contradicts the page it sits in), the
 *      customer's default order contact first;
 *   2. people CIS already knows from other customers (an agency contact:
 *      choosing them links them here as well);
 *   3. anyone else, by typing an email address.
 *
 * Tharstern contacts are no group of their own: the people feed has made each
 * one a person at its customer (v1 completion D1).
 *
 * Choosing does not create anything by itself. It hands back a
 * `PortalContactChoice`; the host calls find-or-create when it acts on the
 * choice (send the quote, place the order). The line under the field says,
 * in plain words, what will happen then, because "a person is created
 * silently" is only acceptable if the person doing it can see it coming.
 *
 * `suggest` is optional: with none, only the customer's own people are
 * offered and typed emails are not accepted, which is what a host without
 * the lookup behind it should do rather than pretend.
 * decision: docs/features/customer-storefront/12_decision_access_for_every_customer.md
 */
import { useState } from "react";
import { ChevronDown, Loader2, Mail, UserPlus, X } from "lucide-react";
import type {
  PortalContactChoice,
  PortalContactSuggestions,
} from "@shared-core/data/customer-portal-access-types";
import {
  displayNameFromEmail,
  groupContactOptions,
} from "@shared-core/domain/customerPortalAccess";
import { usePortalContactSuggestions } from "@shared-core/domain/usePortalContactSuggestions";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../primitives/command";
import { Popover, PopoverContent, PopoverTrigger } from "../primitives/popover";
import { Button } from "../primitives/button";
import { Input } from "../primitives/input";
import { cn } from "../primitives/utils";

export interface PortalContactPickerPerson {
  person_id: string;
  name: string;
  email: string;
  is_default_order_contact?: boolean;
  job_title?: string | null;
}

export interface PortalContactPickerProps {
  customerName: string;
  /** People already at this customer, as the host page knows them. */
  linkedPeople: PortalContactPickerPerson[];
  value: PortalContactChoice | null;
  onChange: (choice: PortalContactChoice | null) => void;
  /** Looks up the customer's people and people CIS knows elsewhere. Absent: the host's own people only. */
  suggest: ((query: string) => Promise<PortalContactSuggestions>) | null;
  /** What the host will do with the choice, to finish "…when you ___". */
  whenActedOn: string;
  placeholder?: string;
  /** The empty option, when picking nobody is allowed (an order's contact). */
  noneLabel?: string;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

function choiceLabel(choice: PortalContactChoice): string {
  return choice.name ? `${choice.name} (${choice.email})` : choice.email;
}

/** What will happen to the person once the host acts, in words. */
export function describeChoiceOutcome(
  choice: PortalContactChoice,
  customerName: string,
  whenActedOn: string,
): string | null {
  switch (choice.kind) {
    case "person":
      return choice.linked_here
        ? null
        : `Already known to us${choice.other_customer_names.length ? `, at ${choice.other_customer_names.join(" and ")}` : ""}. They will be linked to ${customerName} when you ${whenActedOn}.`;
    case "new_email":
      return `A new person. ${choice.email} will be added to ${customerName} when you ${whenActedOn}, and can use Get access with that address to set up their sign-in.`;
  }
}

export function PortalContactPicker({
  customerName,
  linkedPeople,
  value,
  onChange,
  suggest,
  whenActedOn,
  placeholder = "Choose a person, or type an email address…",
  noneLabel,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: PortalContactPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { suggestions, loading, lookupFailed } = usePortalContactSuggestions(
    suggest,
    query,
    open,
  );
  const { linked, known, typedNewEmail } = groupContactOptions({
    query,
    linkedPeople,
    suggestions,
    canAddNewEmail: suggest !== null,
  });
  const nothingToShow =
    linked.length === 0 && known.length === 0 && typedNewEmail === null;

  function choose(choice: PortalContactChoice | null) {
    onChange(choice);
    setOpen(false);
    setQuery("");
  }

  const outcome = value
    ? describeChoiceOutcome(value, customerName, whenActedOn)
    : null;

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-muted-foreground",
            )}
          >
            <span className="truncate text-left">
              {value ? choiceLabel(value) : placeholder}
            </span>
            <span className="ml-2 flex shrink-0 items-center gap-1">
              {value && noneLabel && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={noneLabel}
                  className="rounded p-0.5 hover:bg-accent"
                  onClick={(e) => {
                    e.stopPropagation();
                    choose(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      choose(null);
                    }
                  }}
                >
                  <X className="size-3.5" />
                </span>
              )}
              <ChevronDown className="size-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] min-w-80 p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by name or email, or type a new email"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {loading && (
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                  <Loader2 className="size-3 animate-spin" /> Looking…
                </div>
              )}
              {lookupFailed && (
                <div className="px-3 py-2 text-xs text-destructive">
                  We could not look up contacts. People already at{" "}
                  {customerName} are still listed.
                </div>
              )}
              {linked.length > 0 && (
                <CommandGroup heading={`At ${customerName}`}>
                  {linked.map((p) => (
                    <CommandItem
                      key={p.person_id}
                      value={`person-${p.person_id}`}
                      onSelect={() =>
                        choose({
                          kind: "person",
                          person_id: p.person_id,
                          name: p.name,
                          email: p.email,
                          linked_here: true,
                          other_customer_names: [],
                        })
                      }
                    >
                      <PersonLine
                        name={p.name}
                        email={p.email}
                        hint={p.job_title ?? undefined}
                        isOrderContact={p.is_default_order_contact}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {known.length > 0 && (
                <CommandGroup heading="Already known to us, at other customers">
                  {known.map((p) => (
                    <CommandItem
                      key={p.person_id}
                      value={`known-${p.person_id}`}
                      onSelect={() =>
                        choose({
                          kind: "person",
                          person_id: p.person_id,
                          name: p.display_name,
                          email: p.email,
                          linked_here: false,
                          other_customer_names: p.other_customer_names,
                        })
                      }
                    >
                      <PersonLine
                        name={p.display_name}
                        email={p.email}
                        hint={`Also at ${p.other_customer_names.join(", ")}`}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {typedNewEmail !== null && (
                <CommandGroup heading="Someone else">
                  <CommandItem
                    value="typed-email"
                    onSelect={() =>
                      choose({
                        kind: "new_email",
                        email: typedNewEmail,
                        name: displayNameFromEmail(typedNewEmail),
                      })
                    }
                  >
                    <UserPlus className="size-4 text-muted-foreground" />
                    <span>
                      Add <b>{typedNewEmail}</b> as a new person
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {!loading && nothingToShow && (
                <CommandEmpty>
                  {suggest
                    ? "No one matches. Type their full email address to add them."
                    : `No one at ${customerName} to choose from yet.`}
                </CommandEmpty>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value?.kind === "new_email" && (
        <div className="flex items-center gap-2">
          <Mail className="size-4 shrink-0 text-muted-foreground" />
          <Input
            aria-label="Their name (optional)"
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            placeholder="Their name (optional)"
            className="h-8"
          />
        </div>
      )}
      {outcome && (
        <p className="text-xs text-muted-foreground" role="status">
          {outcome}
        </p>
      )}
    </div>
  );
}

function PersonLine({
  name,
  email,
  hint,
  isOrderContact = false,
}: {
  name: string;
  email: string;
  hint?: string;
  isOrderContact?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate">{name}</span>
        {isOrderContact && (
          <span className="shrink-0 rounded-full border border-primary px-1.5 text-[10px] text-primary">
            Order contact
          </span>
        )}
      </span>
      <span className="truncate text-[11px] text-muted-foreground">
        {email}
        {hint ? ` · ${hint}` : ""}
      </span>
    </div>
  );
}
